import { FileText, CheckCircle2, Clock, CalendarX } from 'lucide-react';

interface HomeworkStatCardsProps {
  totalAssigned: number;
  completedCount: number;
  /** `null` in a week with nothing set — shown as "—" with an empty bar. */
  completedPercent: number | null;
  pendingCount: number;
  pendingPercent: number | null;
  overdueCount: number;
}

export function HomeworkStatCards({
  totalAssigned,
  completedCount,
  completedPercent,
  pendingCount,
  pendingPercent,
  overdueCount,
}: HomeworkStatCardsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4">
      {/* Total Assigned - Indigo Variant */}
      <div className="rounded-2xl bg-indigo-50/90 dark:bg-slate-900 border border-indigo-200/90 dark:border-slate-800 px-5 py-3 shadow-xs hover:border-indigo-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-[#4f46e5] text-white flex items-center justify-center shrink-0 shadow-xs">
          <FileText className="h-6 w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14px] font-bold text-indigo-800 dark:text-indigo-400">Total Assigned</span>
          <span className="text-2xl sm:text-3xl font-black text-indigo-600 dark:text-indigo-400 leading-tight mt-0.5">
            {totalAssigned}
          </span>
          <span className="text-xs text-indigo-600/90 dark:text-slate-400 font-semibold mt-0.5">This Week</span>
        </div>
      </div>

      {/* Completed - Emerald Variant */}
      <div className="rounded-2xl bg-emerald-50/90 dark:bg-slate-900 border border-emerald-200/90 dark:border-slate-800 px-5 py-3 shadow-xs hover:border-emerald-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-[#059669] text-white flex items-center justify-center shrink-0 shadow-xs">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14px] font-bold text-emerald-800 dark:text-emerald-400">Completed</span>
          <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-tight mt-0.5">
            {completedCount}
          </span>
          <div className="flex items-center gap-2 mt-1.5 w-full">
            <span className="text-xs text-emerald-700/90 dark:text-slate-400 font-semibold shrink-0">This Week</span>
            <div className="flex-1 h-1.5 rounded-full bg-emerald-200/70 dark:bg-slate-800 overflow-hidden">
              {completedPercent !== null && (
                <div
                  className="h-full bg-emerald-600 dark:bg-emerald-500 rounded-full transition-all duration-300"
                  style={{ width: `${completedPercent}%` }}
                />
              )}
            </div>
            <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 shrink-0">
              {completedPercent === null ? '0%' : `${completedPercent}%`}
            </span>
          </div>
        </div>
      </div>

      {/* Pending - Amber Variant */}
      <div className="rounded-2xl bg-amber-50/90 dark:bg-slate-900 border border-amber-200/90 dark:border-slate-800 px-5 py-3 shadow-xs hover:border-amber-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-[#d97706] text-white flex items-center justify-center shrink-0 shadow-xs">
          <Clock className="h-6 w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14px] font-bold text-amber-800 dark:text-amber-400">Pending</span>
          <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 leading-tight mt-0.5">
            {pendingCount}
          </span>
          <div className="flex items-center gap-2 mt-1.5 w-full">
            <span className="text-xs text-amber-700/90 dark:text-slate-400 font-semibold shrink-0">This Week</span>
            <div className="flex-1 h-1.5 rounded-full bg-amber-200/70 dark:bg-slate-800 overflow-hidden">
              {pendingPercent !== null && (
                <div
                  className="h-full bg-amber-600 dark:bg-amber-500 rounded-full transition-all duration-300"
                  style={{ width: `${pendingPercent}%` }}
                />
              )}
            </div>
            <span className="text-xs font-bold text-amber-700 dark:text-amber-400 shrink-0">
              {pendingPercent === null ? '0%' : `${pendingPercent}%`}
            </span>
          </div>
        </div>
      </div>

      {/* Overdue - Rose Variant */}
      <div className="rounded-2xl bg-rose-50/90 dark:bg-slate-900 border border-rose-200/90 dark:border-slate-800 px-5 py-3 shadow-xs hover:border-rose-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-[#e11d48] text-white flex items-center justify-center shrink-0 shadow-xs">
          <CalendarX className="h-6 w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14px] font-bold text-rose-800 dark:text-rose-400">Overdue</span>
          <span className="text-2xl sm:text-3xl font-black text-rose-600 dark:text-rose-400 leading-tight mt-0.5">
            {overdueCount}
          </span>
          <span className="text-xs font-bold text-rose-600/90 dark:text-rose-400 mt-0.5">
            Needs Attention
          </span>
        </div>
      </div>
    </div>
  );
}
